import { getBillingCategoryOptions } from "@components/modules/projects/list/Store";
import type { TasksViewStore } from "@components/modules/resource-planning/tasks/store/types";
import { ProjectCategorySelectBottomExtraContent } from "@components/ui-kit/ProjectCategorySelect";
import { t } from "@lingui/macro";
import {
  type SortOrder,
  SqlOperator,
  type TaskOrderByColumnEnum,
  type TasksFilterOptionsQuery,
  TaskWhereColumn,
} from "@src/__generated__/graphql";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { getBooleanFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";

type TClause = {
  order?: SortOrder;
  column: TaskOrderByColumnEnum;
};

export class TasksFiltersStore {
  orderBy = new OrderBy<TaskOrderByColumnEnum>();
  pagination: PaginationState;

  @observable.ref taskStatuses: TasksFilterOptionsQuery["taskStatuses"] = [];
  @observable.ref searchTerm = "";
  /** Text in the search box; `searchTerm` follows it after the debounce. */
  @observable.ref searchInput = "";
  // After the first tasks page restores the search from the URL, tab
  // switches keep the current term, like the panel filters.
  isSearchInitialized = false;

  where = new Filters<TaskWhereColumn>(
    [
      new Filter({
        column: TaskWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: TaskWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.OurWorkBudgetItemId,
        operator: SqlOperator.In,
        title: t`Budget item`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.TaskStatusId,
        operator: SqlOperator.In,
        title: t`Task status`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.TaskPriorityId,
        operator: SqlOperator.In,
        title: t`Priority`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.Deadline,
        operator: SqlOperator.Between,
        title: t`Deadline`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.UserId,
        operator: SqlOperator.In,
        title: t`People`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.TimeTrackingWorkTypeId,
        operator: SqlOperator.In,
        title: t`Position`,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.CreatedByUserId,
        operator: SqlOperator.In,
        title: t`Created by`,
        options: [],
      }),
      new Filter({
        title: t`Project category`,
        column: TaskWhereColumn.ProjectCategoryId,
        operator: SqlOperator.In,
        options: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        column: TaskWhereColumn.Billable,
        operator: SqlOperator.Eq,
        title: t`Billing category`,
        options: getBillingCategoryOptions(),
      }),
      new Filter({
        column: TaskWhereColumn.HideDone,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.AfterDeadline,
        title: t`After deadline`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: TaskWhereColumn.AfterEndDate,
        title: t`After end date`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: TaskWhereColumn.QuickFilter,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.MyTasks,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.MyCurrentTasks,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.MyCreatedTasks,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        column: TaskWhereColumn.MyWatchedTasks,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
      new Filter({
        title: t`Project manager`,
        column: TaskWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        hidden: false,
        options: [],
      }),
      new Filter({
        title: t`Project team leader`,
        column: TaskWhereColumn.ProjectTeamLeaderId,
        operator: SqlOperator.In,
        hidden: false,
        options: [],
      }),
      new Filter({
        hidden: true,
        column: TaskWhereColumn.OnlyParent,
        operator: SqlOperator.Eq,
        options: [],
        value: "true",
      }),
      new Filter({
        column: TaskWhereColumn.StarredTasks,
        operator: SqlOperator.Eq,
        hidden: true,
        options: [],
      }),
    ],
    {
      onChange: () => {
        this.viewStore.resetPagination();
        this.viewStore.fetchTaskIds();
      },
      tableKey: "tasksListing",
      orderBy: this.orderBy,
    },
  );

  constructor(
    private viewStore: TasksViewStore,
    paginationKey = "tasksListing",
  ) {
    this.pagination = new PaginationState(paginationKey);
    makeObservable(this);
  }

  /**
   * Filters are shared between the list and the board, so they always act on
   * the currently rendered view.
   */
  @action.bound setViewStore(viewStore: TasksViewStore) {
    this.viewStore = viewStore;
  }

  /**
   * List and board page differently, so each keeps its own filters store —
   * the view switched to takes over the search and filters of the one left.
   */
  @action.bound syncFrom(source: TasksFiltersStore) {
    this.searchTerm = source.searchTerm;
    this.searchInput = source.searchInput;
    this.isSearchInitialized = source.isSearchInitialized;

    for (const filter of this.where.filters) {
      const sourceFilter = source.where.filtersByColumn.get(filter.column);
      if (sourceFilter) filter.hidden = sourceFilter.hidden;
    }

    this.where.reset([], true);
    this.where.fromURLSearchParam(source.where.asURLSearchParam, {
      force: true,
      skipOnChange: true,
    });
    this.where.selectedTemplateId = source.where.selectedTemplateId;
    this.where.onChange?.();
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  @computed get showSubtasksAsTasks() {
    return (
      (this.searchTerm !== "" && this.searchTerm.length > 0) ||
      this.where.visibleFilters.some((filter) => filter.value.length > 0) ||
      (this.where.filtersByColumn.get(TaskWhereColumn.MyTasks)?.value?.length ??
        0) > 0 ||
      (this.where.filtersByColumn.get(TaskWhereColumn.MyCurrentTasks)?.value
        ?.length ?? 0) > 0 ||
      (this.where.filtersByColumn.get(TaskWhereColumn.MyCreatedTasks)?.value
        ?.length ?? 0) > 0 ||
      (this.where.filtersByColumn.get(TaskWhereColumn.StarredTasks)?.value
        ?.length ?? 0) > 0 ||
      (this.where.filtersByColumn.get(TaskWhereColumn.MyWatchedTasks)?.value
        ?.length ?? 0) > 0
    );
  }

  @computed get doneTasksFilter() {
    return this.where.filtersByColumn.get(TaskWhereColumn.HideDone);
  }

  @computed get hideDoneTasks(): boolean {
    return !!this.doneTasksFilter?.value.length;
  }

  @action.bound toggleDoneTasksFilter() {
    if (this.hideDoneTasks) {
      this.doneTasksFilter?.clear();
    } else {
      this.doneTasksFilter?.setValue("true");
    }
  }

  @action.bound setSearchInput(input: string) {
    this.searchInput = input;
  }

  @action.bound setSearch(term: string) {
    this.searchTerm = term;
    this.searchInput = term;
  }

  @action.bound handleSearchChange(term: string) {
    this.searchTerm = term;
    this.viewStore.resetPagination();
    if (term.length === 0 || term === "") {
      this.where.filtersByColumn
        .get(TaskWhereColumn.OnlyParent)
        ?.setValue("true");
    } else {
      this.where.filtersByColumn
        .get(TaskWhereColumn.OnlyParent)
        ?.setValue("false");
    }
    this.viewStore.fetchTaskIds();
  }

  @action.bound handleOrderChange(clause: TClause) {
    if (this.clauseOrderExists(clause)) {
      if (!clause.order) {
        this.orderBy.setClauses([]);
        return;
      }
      this.orderBy.setClauses([clause]);
    } else {
      this.orderBy.setClauses([]);
    }
    this.viewStore.resetPagination();
    this.viewStore.fetchTasks();
  }

  @computed get selectedProjectsFilterValue(): string[] {
    return (
      this.where.filtersByColumn.get(TaskWhereColumn.ProjectId)?.value ?? []
    );
  }

  @computed get budgetItemsFilter() {
    return this.where.filtersByColumn.get(TaskWhereColumn.OurWorkBudgetItemId);
  }

  @action setFilterOptions(data: TasksFilterOptionsQuery) {
    this.taskStatuses = data.taskStatuses;
    this.where.filtersByColumn.get(TaskWhereColumn.TaskStatusId)?.setOptions(
      data.taskStatuses.map((status) => ({
        value: status.id,
        label: status.name,
      })),
    );
    this.where.filtersByColumn
      .get(TaskWhereColumn.TimeTrackingWorkTypeId)
      ?.setOptions(
        data.timeTrackingWorkTypes.map((status) => ({
          value: status.id,
          label: status.title,
        })),
      );
    this.where.filtersByColumn
      .get(TaskWhereColumn.TaskPriorityId)!
      .setOptions(mapToOptions.priorities.toOptions(data.taskPriorities));
    this.where.filtersByColumn
      .get(TaskWhereColumn.UserId)!
      .setOptions(mapToOptions.projectManagers.toOptions(data.userSimpleMap));
    this.where.filtersByColumn
      .get(TaskWhereColumn.CreatedByUserId)!
      .setOptions(mapToOptions.projectManagers.toOptions(data.userSimpleMap));

    this.where.filtersByColumn
      .get(TaskWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));

    this.where.filtersByColumn.get(TaskWhereColumn.BrandId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
        options: i?.brands.map((brand) => {
          return {
            label: brand.name,
            value: brand.id,
          };
        })!,
      })),
    );

    this.where.filtersByColumn.get(TaskWhereColumn.TeamId)?.setOptions(
      data.teamSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );

    this.where.filtersByColumn
      .get(TaskWhereColumn.ProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );

    this.where.filtersByColumn
      .get(TaskWhereColumn.ProjectTeamLeaderId)
      ?.setOptions(mapToOptions.projectManagers.toOptions(data.userSimpleMap));
  }

  private clauseOrderExists(clause: TClause): clause is Required<TClause> {
    return typeof clause.order !== "undefined";
  }
}
